import { useCallback, useEffect, useState } from "react";
import { useBoolean } from "../../../hooks/use-boolean";
import { IBranch, IBranchCreate, IBranchFilters } from "../../../types/branch";
import { branchOrderOptions, defaultFilters } from "../../../constants/branch";
import { filter, isEqual } from "lodash";
import useTable from "../../../hooks/use-table";
import { IPagination } from "../../../types/pagination";
import { ActiveBranchService, BranchesService, CreateBranchService, DeleteBranchService } from "../../../services/branches";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

const useBranchList = () => {

    // hooks
    const openFilters = useBoolean();
    const openNewBranch = useBoolean();
    const loading = useBoolean(true);
    const table = useTable({
        defaultDirection: branchOrderOptions[0].direction,
        defaultSortBy: branchOrderOptions[0].sortBy
    });

    // states
    const [filters, setFilters] = useState<IBranchFilters>(defaultFilters);
    const [branchesList, setBranchesList] = useState<IPagination<IBranch> | null>(null);

    // rhf
    const NewBranchSchema = Yup.object().shape({
        name: Yup.string().required('Informe o nome da filial').max(255, 'O nome deve conter no máximo 255 caracteres'),
        code: Yup.string().required('Informe o código da filial').max(10, 'O código deve conter no máximo 10 caracteres'),
    });    

    const methods = useForm({
        resolver: yupResolver<IBranchCreate>(NewBranchSchema),
        defaultValues: {
            code: '',
            name: ''
        }
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
        reset,
    } = methods;

    // functions
    const canReset = !isEqual(defaultFilters, filters);

    const handleFilters = useCallback((name: string, value: string) => {
        setFilters((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    }, []);

    const handleResetFilters = useCallback(() => {
        setFilters(defaultFilters);
        getBranches();
    }, []);

    const getBranches = async () => {

        loading.onTrue();

        await BranchesService(table, filters).then(resp => {
            setBranchesList(resp.data);
        }).catch(error => {
            enqueueSnackbar(error?.response?.data?.message || 'Ocorreu um erro', {
                variant: 'error'
            });
        });

        loading.onFalse();

    }

    const onSubmit = handleSubmit(async (data: IBranchCreate) => {

        loading.onTrue();

        await CreateBranchService(data).then(resp => {
            console.log(resp.data);
            openNewBranch.onFalse();
            enqueueSnackbar(resp.data.message);
            reset({
                name: '',
                code: ''
            });            
            getBranches();
        }).catch(error => {
            loading.onFalse();
            enqueueSnackbar(error?.response?.data?.message || 'Ocorreu um erro', {
                variant: 'error'
            });
        });

    });

    const deleteBranch = async (id: number) => {

        loading.onTrue();

        await DeleteBranchService(id).then(resp => {
            enqueueSnackbar(resp.data.message);
            getBranches();
        }).catch(error => {
            loading.onFalse();
            enqueueSnackbar(error?.response?.data?.message || 'Ocorreu um erro', {
                variant: 'error'
            });
        });
    }

    const activeBranch = async (id: number) => {

        loading.onTrue();

        await ActiveBranchService(id).then(resp => {
            enqueueSnackbar(resp.data.message);
            getBranches();
        }).catch(error => {
            loading.onFalse();
            enqueueSnackbar(error?.response?.data?.message || 'Ocorreu um erro', {
                variant: 'error'
            });
        });
    }

    useEffect(() => {
        getBranches();
    }, [table.page, table.direction, table.sortBy, table.pageSize])

    return {
        openFilters,
        filters,
        canReset,
        handleFilters,
        handleResetFilters,
        table,
        loading,
        branchesList,
        getBranches,
        openNewBranch,
        methods,
        isSubmitting,
        onSubmit,
        deleteBranch,
        activeBranch
    }

}

export default useBranchList;